import { FlashList } from '@shopify/flash-list';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, RefreshControl, View } from 'react-native';

import { AlphaSortSlider } from '@components/AlphaSortSlider/AlphaSortSlider';
import { AvatarProps } from '@components/Avatar/Avatar';
import EmptyStateNoContacts from '@components/EmptyState/EmptyStateNoContacts';
import EmptyStateNoSearch from '@components/EmptyState/EmptyStateNoSearch';
import ContactNew from '@components/Invite/ContactNew';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { FlashListPlainViewCell } from '@components/shared/FlashListPlainViewCell';
import {
  MarginTopM,
  MarginBottomListFooter,
  PaddingBottomListFooter,
} from '@components/shared/ListSpacers';
import { ContactRowSkeleton } from '@components/shared/skeletons/ContactRowSkeleton';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import { UserSearchType } from '@components/TaskList/TaskInviteOrAssignee.web';
import { TaskMemberRole } from '@graphql/generated';
import { useContactListSpacers } from '@hooks/useContactListSpacers';
import { useFontScale } from '@hooks/useFontScale';
import useMe from '@hooks/useMe';
import theme from '@themes/theme';
import {
  getInviteItemLayoutSize,
  INVITE_ROW_CELL_HEIGHT,
} from '@utils/inviteContactListLayout';

import Contact, { ContactType } from './Contact';

export interface ContactListProps {
  contacts: ContactType[];
  taskTagUsers?: ContactType[];
  onPress: (item: ContactType) => void;
  filterVal?: string;
  alphabetize?: boolean;
  headerSpace?: keyof typeof theme.spacing;
  footerSpace?: number;
  isEditing?: boolean;
  isInviting?: boolean;
  isFromChatInviteFriends?: boolean;
  loading?: boolean;
  selected?: string[];
  shouldDisplayPhoneContactLabel?: boolean;
  disabledRoles?: string[];
  showMessageIcon?: boolean;
  onMessagePress?: (item: ContactType) => void;
  /** TA-10276: id of the contact whose "Send Message" is currently in
   * flight — shows a spinner on that row's message icon instead of the
   * default icon. */
  messageLoadingId?: string;
  showEmptyStateNoSearch?: boolean;
  showEmptyStateNoContacts?: boolean;
  disabledUsersId?: string[];
  refreshing?: boolean;
  onRefresh?: () => void;
  hasNextPage?: boolean;
  loadMore?: () => void;
  canClickMe?: boolean;
  avatarSize?: AvatarProps['size'];
  isTaskMember?: boolean;
  currentUserRole?: TaskMemberRole;
  openModal?: (item: ContactType) => void;
  selectedUserList?: UserSearchType[];
  isNewChatFromTask?: boolean;
  isSelectedBoxOnRight?: boolean;
  showEmail?: boolean;
  checkboxOnRight?: boolean;
  /** TA-10279: opt-in — swaps the loading spinner for 3 skeleton rows.
   * Defaults to false so existing (native) callers keep the pre-existing
   * spinner; web callers adopting the TA-10089 skeleton pattern pass true. */
  showLoadingSkeleton?: boolean;
  /** Overrides the row rendered by `showLoadingSkeleton` — defaults to
   * `ContactRowSkeleton`. Callers whose rows look different (e.g. the Chat
   * Members list's `avatarSize='medium'`, no alphabet header) can pass a
   * closer-matching composite instead. */
  skeletonRow?: (index: number) => React.ReactElement;
}

// Shared by every ContactList screen, so it stays at its long-standing value:
// their rows differ (role labels, emails, other avatar sizes) and have not
// been re-measured. The Invite-from-Phone list uses its own measured height.
const ESTIMATED_CONTACT_ITEM_SIZE = 40;

// TA-11104: FlashList keeps rows within this distance of the viewport mounted
// and reuses the rest. It reads the value once at mount, so it must be a
// constant. On iOS a reused row can land at the wrong position (see
// FlashListPlainViewCell), which is how the 250px default blanked the
// Invite-from-Phone list on a far scroll. At 3000 the lists here that still
// use FlashList's own cell reuse rows far less often; 10000 kept nearly the
// whole Invite-from-Phone list mounted and showed brief white gaps right
// after open.
const CONTACT_LIST_DRAW_DISTANCE = 3000;

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  taskTagUsers,
  filterVal,
  alphabetize = false,
  onPress,
  headerSpace,
  footerSpace,
  isEditing,
  isInviting,
  isFromChatInviteFriends = false,
  loading,
  selected,
  shouldDisplayPhoneContactLabel,
  disabledRoles,
  disabledUsersId,
  showMessageIcon = false,
  showEmptyStateNoSearch = false,
  showEmptyStateNoContacts = false,
  onMessagePress,
  messageLoadingId,
  refreshing = false,
  onRefresh,
  hasNextPage = false,
  loadMore,
  canClickMe = false,
  avatarSize,
  isTaskMember = false,
  currentUserRole,
  openModal,
  selectedUserList,
  isNewChatFromTask = false,
  isSelectedBoxOnRight = false,
  showEmail = false,
  checkboxOnRight = false,
  showLoadingSkeleton = false,
  skeletonRow = () => <ContactRowSkeleton />,
}) => {
  const { t } = useTranslation();
  const { me } = useMe();

  const [sortedFilteredContacts, setSortedFilteredContacts] =
    useState<ContactType[]>(contacts);

  const [alphabetizedSectionList, setAlphabetizedSectionList] = useState<
    (ContactType | string)[]
  >([]);
  const [alphabetizeList, setAlphabetizeList] = useState<string[]>([]);
  const flashListRef = useRef<FlashList<string>>(null);
  // TA-11104: iOS only, where FlashList's own cell loses its position (see
  // FlashListPlainViewCell). Limited to the Invite-from-Phone list: it is the
  // one list here with exact row sizes, and the one verified this way.
  const usesPlainViewCells = isFromChatInviteFriends && Platform.OS === 'ios';
  const { fontScale } = useFontScale();
  // Exact sizes (see inviteContactListLayout) so rows are placed correctly on
  // the first paint.
  const overrideInviteItemLayout = useCallback(
    (layout: { size?: number }, item: ContactType | string) => {
      const size = getInviteItemLayoutSize(item, fontScale);
      if (size !== undefined) layout.size = size;
    },
    [fontScale]
  );
  const [selectedContact, setSelectedContact] = useState<
    ContactType | undefined
  >();
  const [friendRequestSuccess, setFriendRequestSuccess] = useState(false);
  const taskTagUserIds = taskTagUsers?.map((i) => i.id);

  const { renderListHeader, renderListFooter } = useContactListSpacers({
    headerSpace,
    footerSpace,
    showEmptyStateNoSearch,
  });

  useEffect(() => {
    let next: ContactType[];

    if (isTaskMember) {
      const emails = contacts.filter((item) =>
        filterVal
          ? item.__typename === 'StringSearchType' &&
            item.title?.toLowerCase().includes(filterVal?.toLowerCase())
          : item.__typename === 'StringSearchType'
      );
      const sortContacts =
        filterVal && filterVal !== ''
          ? contacts.filter(
              (item) =>
                item.__typename === 'str' ||
                (item.__typename !== 'StringSearchType' &&
                  (item.displayName ?? item.name)
                    ?.toLowerCase()
                    .includes(filterVal?.toLowerCase()))
            )
          : contacts
              .filter((item) => item.__typename !== 'StringSearchType')
              .sort((a, b) =>
                a.displayName
                  ?.toLowerCase()
                  .localeCompare(b.displayName?.toLowerCase())
              );
      const taskMembers = sortContacts
        .filter(
          (item) => disabledUsersId?.includes(item.id) && !item.isTaskTagUser
        )
        .sort((a, b) =>
          a.displayName
            ?.toLowerCase()
            .localeCompare(b.displayName?.toLowerCase())
        );
      const notTaskMembers = sortContacts.filter(
        (item) => !disabledUsersId?.includes(item.id) || item.isTaskTagUser
      );

      const hasTasktagUsers = sortContacts.some((item) => item.isTaskTagUser);

      const notTaskMembers1 = hasTasktagUsers
        ? notTaskMembers
        : notTaskMembers.filter((item) => item.__typename !== 'str');

      next =
        filterVal && filterVal !== ''
          ? [...emails, ...taskMembers, ...notTaskMembers1]
          : [
              ...taskMembers,
              ...notTaskMembers.filter((item) => !item.isTaskTagUser),
            ];
    } else {
      const filtered = filterVal
        ? contacts.filter((contact) =>
            contact.displayName
              ?.toLowerCase()
              .includes(filterVal?.toLowerCase())
          )
        : contacts;

      next = filtered
        .slice()
        .sort((a, b) =>
          a.displayName
            ?.toLowerCase()
            .localeCompare(b.displayName?.toLowerCase())
        );
    }

    // Skip update when content is identical — prevents re-render loops when
    // the parent passes a new array reference with the same items (e.g. []).
    setSortedFilteredContacts((prev) => {
      if (prev.length === next.length && prev.every((c, i) => c === next[i])) {
        return prev;
      }
      return next;
    });
  }, [contacts, filterVal]);

  useEffect(() => {
    if (!alphabetize) return;
    // Only call when filteredContacts change, not on length alone
    if (sortedFilteredContacts.length > 0 || filterVal !== '') {
      getAlphabetizedSectionList();
    }
  }, [sortedFilteredContacts, filterVal]);

  const sortContacts = () => {
    const numsSpecials: ContactType[] = []; // numbers and ASCII before A
    const letters: ContactType[] = []; // letters
    const asterisks: ContactType[] = []; // ASCII after Z

    sortedFilteredContacts.forEach((s) => {
      const firstChar = s.displayName?.[0];
      if (!firstChar) return;

      const charCode = firstChar.charCodeAt(0);

      if (/[a-z]/i.test(firstChar)) {
        letters.push(s); // Letters A-Z or a-z
      } else if (charCode < 65) {
        numsSpecials.push(s); // Characters before 'A'
      } else {
        asterisks.push(s); // Characters after 'Z'
      }
    });

    // Helper function to sort by displayName's first character's char code
    const sortByCharCode = (arr: ContactType[]) =>
      arr.sort(
        (a, b) =>
          a.displayName[0].charCodeAt(0) - b.displayName[0].charCodeAt(0)
      );

    // Sort each category
    sortByCharCode(numsSpecials);
    letters.sort((a, b) =>
      a.displayName[0]
        .toUpperCase()
        .localeCompare(b.displayName[0].toUpperCase())
    );
    sortByCharCode(asterisks);

    return { letters, numsSpecials, asterisks };
  };

  const getAlphabetizedSectionList = () => {
    const tmpAlphabetizeList = ['#'];
    const { letters, numsSpecials, asterisks } = sortContacts();

    const formattedAlpha: (ContactType | string)[] = [];
    const formattedNumSpecial: (ContactType | string)[] = numsSpecials.length
      ? ['#', ...numsSpecials]
      : [];
    const formattedAsterisk: (ContactType | string)[] = asterisks.length
      ? ['*', ...asterisks]
      : [];

    letters.forEach((obj) => {
      const key = obj.displayName.charAt(0).toUpperCase();

      // Check if the key is already added, if not, add it before the contact
      if (!formattedAlpha.includes(key)) {
        formattedAlpha.push(key);
      }
      formattedAlpha.push(obj);
    });

    // Add A-Z to the alphabetize list
    for (
      let charCode = 'A'.charCodeAt(0);
      charCode <= 'Z'.charCodeAt(0);
      charCode++
    ) {
      tmpAlphabetizeList.push(String.fromCharCode(charCode));
    }

    // Add asterisk section to the alphabetize list
    tmpAlphabetizeList.push('*');

    setAlphabetizeList(tmpAlphabetizeList);
    setAlphabetizedSectionList([
      ...formattedNumSpecial,
      ...formattedAlpha,
      ...formattedAsterisk,
    ]);
  };

  if (loading)
    return showLoadingSkeleton ? (
      <SkeletonList count={3} row={skeletonRow} />
    ) : (
      <ActivityIndicatorLoading testID='loading-indicator' />
    );
  const toggleFriendRequestSuccess = (friendRequest: boolean) => {
    setFriendRequestSuccess(friendRequest);
    onRefresh?.();
  };

  const renderContact = (item: ContactType, disabled?: boolean) => {
    const isTaskTagUser = taskTagUserIds?.includes(item.id);
    return isTaskMember ? (
      <ContactNew
        key={item.id}
        toggleFriendRequestSuccess={
          isTaskTagUser ? toggleFriendRequestSuccess : () => {}
        }
        isTaskTagUser={isTaskTagUser}
        contact={{ ...item, roleLabel: item.roleLabel, avatarSize: avatarSize }}
        filterVal={filterVal}
        onPress={onPress}
        isEditing={isEditing}
        isSelected={
          selected?.some((i) => i === (item.title ?? item.id)) ||
          item.isAssigned
        }
        selectedUserList={selectedUserList}
        isInviting={isInviting}
        shouldDisplayPhoneContactLabel={shouldDisplayPhoneContactLabel}
        disabled={
          disabled ||
          disabledUsersId?.includes(
            item.__typename === 'StringSearchType' ? item.title : item.id
          ) ||
          item.isAssigned ||
          isTaskTagUser
        }
        disabledUsersId={disabledUsersId}
        showMessageIcon={showMessageIcon}
        onMessagePress={() => onMessagePress?.(item)}
        currentUserRole={currentUserRole}
        openModal={openModal}
        isNewChatFromTask={isNewChatFromTask}
        showEmail={showEmail}
        isSelectedBoxOnRight={checkboxOnRight || isSelectedBoxOnRight}
      />
    ) : (
      <Contact
        isSelectedBoxOnRight={checkboxOnRight || isSelectedBoxOnRight}
        showEmail={showEmail}
        key={item.id}
        toggleFriendRequestSuccess={
          isTaskTagUser ? toggleFriendRequestSuccess : () => {}
        }
        isTaskTagUser={isTaskTagUser}
        contact={{ ...item, roleLabel: item.roleLabel, avatarSize: avatarSize }}
        filterVal={filterVal}
        onPress={() => {
          if (isFromChatInviteFriends === false) {
            if (!isEditing) {
              setSelectedContact((current) => {
                return current?.id === item.id ? undefined : item;
              });
            }
          }

          onPress(item);
        }}
        isEditing={isEditing}
        isSelected={selected?.some((i) => i === item.id) || item.isAssigned}
        isInviting={isInviting}
        shouldDisplayPhoneContactLabel={shouldDisplayPhoneContactLabel}
        disabled={disabled || item.isAssigned || isTaskTagUser}
        showMessageIcon={showMessageIcon}
        onMessagePress={() => onMessagePress?.(item)}
        isMessageLoading={!!messageLoadingId && item.id === messageLoadingId}
      />
    );
  };

  const contactsAndUsersList = () => {
    return [
      ...sortedFilteredContacts,
      t('shared:moreOnTaskTag'),
      ...(taskTagUsers || []),
    ];
  };

  if (taskTagUsers && taskTagUsers.length > 0) {
    // section list

    return (
      <View style={{ flex: 1 }}>
        <FlashList
          showsVerticalScrollIndicator={false}
          keyboardDismissMode='on-drag'
          ItemSeparatorComponent={MarginTopM}
          ListFooterComponent={MarginBottomListFooter}
          data={contactsAndUsersList()}
          renderItem={({ item }) => {
            if (typeof item === 'string') {
              return (
                <Box
                  mt='s'
                  mb='s'
                  marginHorizontal='m'
                  pt='l'
                  borderTopWidth={1}
                  borderColor='grey02'>
                  <Text variant='labelSmall'>{item}</Text>
                </Box>
              );
            } else {
              return renderContact(item);
            }
          }}
          getItemType={(item) => {
            // To achieve better performance, specify the type based on the item
            return typeof item === 'string' ? 'sectionHeader' : 'row';
          }}
          estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
          drawDistance={CONTACT_LIST_DRAW_DISTANCE}
          refreshing={loading}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          onEndReached={hasNextPage ? loadMore : undefined}
        />

        {friendRequestSuccess && Platform.OS !== 'web' && (
          <View style={{ marginBottom: theme.spacing.l }}>
            <Box
              backgroundColor='black'
              opacity={0.8}
              paddingHorizontal='s'
              paddingVertical='xs'
              borderRadius='xs'
              alignSelf='center'>
              <Text
                color='white'
                variant='labelSmall'
                lineHeight={16}
                textAlign='center'>
                {t('models:chat.connectionRequestSend')}
              </Text>
            </Box>
          </View>
        )}
      </View>
    );
  }

  const getListEmptyComponent = () => {
    if (showEmptyStateNoSearch) {
      return (
        <Box
          px={Platform.OS !== 'web' ? undefined : 'l'}
          style={{ marginTop: -theme.spacing.listFooter / 2 }}>
          <EmptyStateNoSearch showSmall />
        </Box>
      );
    }
    if (showEmptyStateNoContacts) {
      return (
        <Box
          px={Platform.OS !== 'web' ? undefined : 'l'}
          style={{ marginTop: -theme.spacing.listFooter / 2 }}>
          <EmptyStateNoContacts showSmall />
        </Box>
      );
    }
    return undefined;
  };

  return (
    <>
      {alphabetize ? (
        <Box flex={1} position='relative'>
          <FlashList
            ref={flashListRef}
            extraData={selected}
            showsVerticalScrollIndicator={false}
            estimatedItemSize={
              isFromChatInviteFriends
                ? INVITE_ROW_CELL_HEIGHT
                : ESTIMATED_CONTACT_ITEM_SIZE
            }
            drawDistance={CONTACT_LIST_DRAW_DISTANCE}
            overrideItemLayout={
              isFromChatInviteFriends ? overrideInviteItemLayout : undefined
            }
            disableAutoLayout={usesPlainViewCells}
            CellRendererComponent={
              usesPlainViewCells ? FlashListPlainViewCell : undefined
            }
            keyboardDismissMode='on-drag'
            ItemSeparatorComponent={MarginTopM}
            ListFooterComponent={PaddingBottomListFooter}
            ListEmptyComponent={getListEmptyComponent()}
            data={alphabetizedSectionList}
            renderItem={({ item }) => {
              if (typeof item === 'string') {
                return (
                  <Box
                    backgroundColor='grey01'
                    paddingVertical='xs'
                    paddingLeft='m'>
                    <Text color='textPrimary' variant='labelEmphasized'>
                      {item}
                    </Text>
                  </Box>
                );
              }
              // On the web, keep clicking after clicking the contact
              else if (onMessagePress) {
                return renderContact(item, false);
              } else {
                const disabled =
                  (!!item.roleLabel &&
                    disabledRoles?.includes(item.roleLabel)) ||
                  selectedContact?.id === item.id ||
                  disabledUsersId?.includes(item.id) ||
                  (item.id === me?.id && !canClickMe);

                return renderContact(item, disabled);
              }
            }}
            getItemType={(item) => {
              // To achieve better performance, specify the type based on the item
              return typeof item === 'string' ? 'sectionHeader' : 'row';
            }}
          />
          {Platform.OS !== 'web' && alphabetizedSectionList.length > 0 && (
            <AlphaSortSlider
              flashListRef={flashListRef}
              alphabetizeList={alphabetizeList}
              animated={!isFromChatInviteFriends}
            />
          )}
        </Box>
      ) : (
        <FlashList
          showsVerticalScrollIndicator={false}
          extraData={{ selected, contacts, disabledUsersId }}
          estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
          drawDistance={CONTACT_LIST_DRAW_DISTANCE}
          keyboardDismissMode='on-drag'
          data={sortedFilteredContacts}
          renderItem={({ item }) =>
            renderContact(item, disabledUsersId?.includes(item.id))
          }
          ItemSeparatorComponent={MarginTopM}
          ListHeaderComponent={renderListHeader}
          ListFooterComponent={renderListFooter}
          ListEmptyComponent={
            showEmptyStateNoSearch ? (
              <Box
                px={Platform.OS !== 'web' ? undefined : 'l'}
                style={{ marginTop: -theme.spacing.listFooter / 2 }}>
                <EmptyStateNoSearch showSmall />
              </Box>
            ) : undefined
          }
        />
      )}
    </>
  );
};

export default ContactList;
